import type { ClockWords, GoalModel } from '@/api/sideline';

/** The two sports the trader pages serve. Same service code, same JSON — only the words differ. */
export type Sport = 'soccer' | 'basketball';

/**
 * The handful of visible words that would read wrong for the other sport. The long help texts are
 * written for soccer and stay as they are.
 */
export interface SportLabels {
  /** Page titles: "Sideline", "Sideline Monitor", "Sideline Review". */
  name: string;
  /** Per-page browser storage keys: `${slug}-review`. */
  slug: 'sideline' | 'courtside';
  /** The service the page talks to. */
  service: string;
  /** "goal" / "score" — what a tap reports. */
  goal: string;
  /** "match" / "game". */
  match: string;
  /** "full time" / "end of the game". */
  fullTime: string;
  /** What `describeClock` says before and after the game. */
  clock: ClockWords;
  /** The arm-form URL placeholder. */
  urlPlaceholder: string;
  /** Whether the service sells what the taps buy. The basketball service never sells. */
  autoSell: boolean;
}

export const SPORT_LABELS: Record<Sport, SportLabels> = {
  soccer: {
    name: 'Sideline',
    slug: 'sideline',
    service: 'sideline-trader',
    goal: 'goal',
    match: 'match',
    fullTime: 'full time',
    clock: { kicksOff: 'kicks off', fullTime: 'full time' },
    urlPlaceholder: 'Polymarket match URL — or leave empty and give a Kalshi event',
    autoSell: true
  },
  basketball: {
    name: 'Courtside',
    slug: 'courtside',
    service: 'courtside-trader',
    goal: 'score',
    match: 'game',
    fullTime: 'end of the game',
    clock: { kicksOff: 'tips off', fullTime: 'end of the game' },
    urlPlaceholder: 'https://polymarket.com/sports/nba/nba-chi-den-2026-10-11',
    autoSell: false
  }
};

/**
 * The fitted model in a few characters. Soccer fits goal rates per side (`armed` / `opponent` xG);
 * basketball fits a margin and a total, each with its spread. The JSON is typed as the soccer shape,
 * so the basketball fields are read behind guards rather than widening the exported type.
 */
export function describeModel(model: GoalModel | null | undefined): string {
  if (!model) {
    return 'no model';
  }
  const fields = model as unknown as Partial<Record<string, number | null>>;
  if (typeof fields.armed === 'number' && typeof fields.opponent === 'number') {
    return `model ${fields.armed.toFixed(2)}–${fields.opponent.toFixed(2)} xG`;
  }
  if (typeof fields.margin === 'number') {
    const margin = `${fields.margin >= 0 ? '+' : ''}${fields.margin.toFixed(1)}`;
    const sigma = typeof fields.sigma === 'number' ? ` ±${fields.sigma.toFixed(0)}` : '';
    const total = typeof fields.total === 'number' ? ` · total ${fields.total.toFixed(0)}` : '';
    return `margin ${margin}${sigma}${total}`;
  }
  return 'model fitted';
}
